"use client";

import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useMenu } from "@/lib/menu/MenuProvider";
import { menus } from "@/lib/menu/menus";
import { playSound } from "@/lib/sound";
import { STATUS_TEXT } from "./data";
import { useSelf } from "./self";
import {
  BellIcon,
  ChevronDownIcon,
  GearIcon,
  HeadphonesIcon,
  MicIcon,
} from "./icons";
import { Pfp } from "./Pfp";
import { IconButton } from "./ui";

/* Clickable identity cluster — pfp, display name / username#tag, and a tag
   chip on mobile. Shared by the desktop bar and the mobile island. */
function Identity({ mobile }: { mobile: boolean }) {
  const menu = useMenu();
  const self = useSelf();
  const downAt = useRef<{ x: number; y: number } | null>(null);
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-label="Your profile"
      /* Mobile fires on a pointer-up tap check, not click — the keyboard
         dismiss + viewport resize on first tap eats the click event, so a
         fresh tap never opened the sheet. Pointer events still fire, and
         real page-drags get pointer-captured to the track so pointerup
         never reaches us. */
      onPointerDown={(e) => {
        if (mobile) downAt.current = { x: e.clientX, y: e.clientY };
      }}
      onPointerUp={(e) => {
        if (!mobile) return;
        const d = downAt.current;
        downAt.current = null;
        if (
          d &&
          Math.abs(e.clientX - d.x) < 10 &&
          Math.abs(e.clientY - d.y) < 10
        ) {
          menu.popup(menus.profile);
        }
      }}
      onClick={(e) => {
        /* desktop — dropup anchored to the whole bar so the card is
           flush left/right with it */
        if (!mobile) {
          const bar = e.currentTarget.closest(".chat-profile");
          menu.open(menus.profile, (bar ?? e.currentTarget) as HTMLElement);
        }
      }}
      className={`group flex min-w-0 items-center gap-2 rounded-lg py-0.5 pr-1 text-left transition duration-200 hover:bg-raise active:scale-[0.98] ${
        mobile ? "" : "w-full"
      }`}
    >
      <Pfp status={self.status} size={36} src="/pfp.png" />
      <span className="min-w-0 leading-tight">
        <span className="block truncate text-title font-semibold text-fg">
          Display Name
        </span>
        {/* hover roll — username slides up, status rolls in from below
            (same swap as the dm rows) */}
        <span className="relative block h-3.5 overflow-hidden text-micro leading-3.5 text-fg-mute">
          <span className="absolute inset-x-0 truncate transition-all duration-200 ease-out group-hover:-translate-y-3.5 group-hover:opacity-0">
            username#0000
          </span>
          <span className="absolute inset-x-0 translate-y-3.5 truncate opacity-0 transition-all duration-200 ease-out group-hover:translate-y-0 group-hover:opacity-100">
            {STATUS_TEXT[self.status].label.toLowerCase()}
          </span>
        </span>
      </span>
      {/* desktop — open-state chevron, part of the identity button */}
      {mobile ? null : (
        <ChevronDownIcon
          className={`ml-auto h-3 w-3 shrink-0 text-fg-faint transition-[rotate] duration-150 ${
            menu.isOpen("profile") ? "rotate-180" : ""
          }`}
        />
      )}
    </button>
  );
}

function Bar({ mobile }: { mobile: boolean }) {
  const menu = useMenu();
  const [muted, setMuted] = useState(false);
  const [deafened, setDeafened] = useState(false);

  /* --profile-tools-w: the controls zone's real width (divider → bar's
     right edge) drives the mic/output menus' docked span. measured once
     per layout because the chevron halves collapse under 320px and the
     zone shrinks with them */
  useEffect(() => {
    if (mobile) return;
    const el = document.querySelector(".chat-profile");
    const div = el?.querySelector("span.bg-line");
    if (!el || !div) return;
    const sync = () => {
      document.documentElement.style.setProperty(
        "--profile-tools-w",
        `${el.getBoundingClientRect().right - div.getBoundingClientRect().left}px`,
      );
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, [mobile]);

  /* Ctrl Shift M → mute, Ctrl Shift D → deafen. desktop only — the
     mobile island mounts this component too and its listener would
     double-fire the sound */
  useEffect(() => {
    if (mobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || !e.shiftKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "m") {
        e.preventDefault();
        playSound(muted ? "unmute" : "mute");
        setMuted((m) => !m);
      } else if (k === "d") {
        e.preventDefault();
        playSound(deafened ? "undeafen" : "deafen");
        setDeafened((d) => !d);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobile, muted, deafened]);

  if (mobile) {
    return (
      <div className="absolute bottom-4 left-1/2 z-30 flex max-w-[calc(100%-24px)] -translate-x-1/2 items-center gap-1 rounded-2xl bg-slab p-1.5 ring-1 ring-line shadow-[0_8px_24px_rgb(0_0_0/0.45)] animate-pop">
        <Identity mobile />
        <IconButton
          label="Notifications"
          tip={{ title: "Notifications", icon: BellIcon }}
          onClick={(e) => menu.open(menus.notifications, e.currentTarget)}
        >
          <BellIcon className="h-4.5 w-4.5" />
        </IconButton>
        <IconButton
          label="Settings"
          tip={{ title: "Settings", icon: GearIcon }}
          onClick={() => menu.popup(menus.settings)}
        >
          <GearIcon className="h-4.5 w-4.5" />
        </IconButton>
      </div>
    );
  }

  /* right-click a control for its quick menu; the split chevron on each
     left-clicks it open (auto-hidden via container query when the bar
     squeezes too narrow) */
  const quickMenu =
    (def: (typeof menus)[keyof typeof menus]) =>
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      menu.open(def, e.currentTarget);
    };

  /* icon + chevron as two halves with a bare gap — hovering either lifts
     both bgs (group-hover), pressed washes both red, chevron flips while
     open */
  const split = (p: {
    def: (typeof menus)[keyof typeof menus];
    label: string;
    arrowLabel: string;
    pressed: boolean;
    tip: Parameters<typeof IconButton>[0]["tip"];
    onToggle: () => void;
    children: ReactNode;
  }) => {
    const opened = menu.isOpen(p.def.id);
    return (
      <div className="group flex items-center gap-0.5">
        <IconButton
          label={p.label}
          flat
          danger={p.pressed}
          aria-pressed={p.pressed}
          tip={p.tip}
          onClick={p.onToggle}
          onContextMenu={quickMenu(p.def)}
          className={`split-icon rounded-r-none ${
            p.pressed ? "bg-red-500/15" : "group-hover:bg-raise"
          }`}
        >
          {p.children}
        </IconButton>
        <button
          type="button"
          aria-label={p.arrowLabel}
          aria-haspopup="menu"
          aria-expanded={opened}
          className={`profile-arrow grid h-8 w-4 place-items-center rounded-md rounded-l-none transition-colors duration-200 ${
            p.pressed
              ? "bg-red-500/15 text-red-400 hover:text-red-300"
              : "text-fg-faint group-hover:bg-raise hover:text-fg"
          }`}
          onClick={(e) => menu.open(p.def, e.currentTarget)}
        >
          <ChevronDownIcon
            className={`h-2.5 w-2.5 transition-[rotate] duration-150 ${
              opened ? "rotate-180" : ""
            }`}
          />
        </button>
      </div>
    );
  };

  return (
    <div className="chat-profile">
      <div className="min-w-0 flex-1">
        <Identity mobile={false} />
      </div>

      <span aria-hidden className="mx-0.5 h-5 w-px shrink-0 bg-line" />

      <div className="flex shrink-0 items-center gap-0.5">
        {split({
          def: menus.mic,
          label: muted ? "Unmute microphone" : "Mute microphone",
          arrowLabel: "Mic options",
          pressed: muted,
          tip: {
            title: muted ? "Unmute" : "Mute",
            icon: MicIcon,
            hint: "Ctrl Shift M",
          },
          onToggle: () => {
            setMuted((m) => !m);
            playSound(muted ? "unmute" : "mute");
          },
          children: <MicIcon crossed={muted} className="h-4.5 w-4.5" />,
        })}
        {split({
          def: menus.output,
          label: deafened ? "Undeafen" : "Deafen",
          arrowLabel: "Output options",
          pressed: deafened,
          tip: {
            title: deafened ? "Undeafen" : "Deafen",
            icon: HeadphonesIcon,
            hint: "Ctrl Shift D",
          },
          onToggle: () => {
            setDeafened((d) => !d);
            playSound(deafened ? "undeafen" : "deafen");
          },
          children: (
            <HeadphonesIcon crossed={deafened} className="h-4.5 w-4.5" />
          ),
        })}
        {/* settings is click-only — right-click opens quick settings, no
            dropdown arrow */}
        <IconButton
          label="User settings"
          tip={{
            title: "User settings",
            icon: GearIcon,
            description: "Right-click for quick settings.",
          }}
          onClick={() => menu.popup(menus.settings)}
          onContextMenu={quickMenu(menus.quickSettings)}
        >
          <GearIcon className="h-4.5 w-4.5" />
        </IconButton>
      </div>
    </div>
  );
}

export function ProfileBar({ mobile = false }: { mobile?: boolean }) {
  return <Bar mobile={mobile} />;
}
